import { Tooltip } from "@xola/ui-kit";
import { isEquipmentItem, now } from "common";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    CheckInVerificationStatus,
    Event,
    Experience,
    ExperiencePurchaseItem,
    FormGuest,
    Guest,
    GuestCheckInAction,
    GuestStatus,
    Purchase,
} from "types";
import { api } from "../../../api/api";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { updateTimeslotCache } from "../../Event/EditEventCapacityModal";
import { GuestNoShowModal } from "../GuestNoShowModal";
import { CheckInButton } from "./CheckInButton";
import { getCheckInStatusInfo, isItemLevelCheckIn } from "./CheckInHelper";
import { useDashboardTableStore } from "@/components/Dashboard/DashboardTable/useDashboardTableStore";
import { getGuestsToBeCheckedIn } from "@/components/PurchaseModification/CheckInPurchaseGuests/CheckInPurchaseGuestMenu";
import {
    useCheckIfMemberVerificationRequired,
    useMemberVerificationModal,
} from "@/components/PurchaseModification/CheckInPurchaseGuests/useMemberVerificationModal";
import {
    getVerificationTagUpdateAction,
    useVerificationTags,
} from "@/components/PurchaseModification/CheckInPurchaseGuests/useVerificationTags";
import { CheckInAndPrintTicket } from "@/components/Settings/App/PurchaseCreate/CheckInAndPrintTicket";
import { useVisibility } from "@/hooks/useVisibility";
import { getSeller } from "@/utils/getSeller";

enum ButtonSource {
    NONE = "none",
    GUEST_CHECK_IN = "guestCheckIn",
    GROUP_CHECK_IN = "guestGroupCheckIn",
    GUEST_NO_SHOW = "guestNoShow",
    GROUP_NO_SHOW = "groupNoShow",
    GROUP_RETURN_EQUIPMENT = "groupReturnEquipment",
    GUEST_RETURN_EQUIPMENT = "guestReturnEquipment",
}

interface SaveGuestStatusPayload {
    guestStatus: GuestStatus;
    guests: Guest[];
    purchaseId: ID;
    purchaseItemID: ID;
    verificationStatus?: CheckInVerificationStatus;
    /** Equipment without collect-demographics has no guests; status is applied at the item level. XS-1821. */
    isItemLevel?: boolean;
}

export interface CheckInPurchaseGuestMenuProps {
    purchase: Purchase;
    purchaseItem: ExperiencePurchaseItem;
    isOrganizer: boolean;
    guest?: Guest;
}

export const CheckInPurchaseGuestMenu = memo(
    ({ purchase, purchaseItem, guest, isOrganizer = false }: CheckInPurchaseGuestMenuProps) => {
        const invalidate = useInvalidate();
        const seller = getSeller();
        const isTicketPrintingEnabled = seller?.preferences?.ticket?.enabled;
        const { isGranted } = useAuthorization();
        const [source, setSource] = useState<ButtonSource>(ButtonSource.GROUP_NO_SHOW);
        const [noShowGuestDetails, setGuestNoShowDetails] = useState({ count: 1, guestId: "" });

        const { data: experience } = useResource<Experience>(["products", purchaseItem.product.id]);
        const { data: event } = useResource<Event>(["events", purchaseItem.event.id]);

        const { isMemberVerificationRequired } = useCheckIfMemberVerificationRequired(purchaseItem);
        const { verifyMember, MemberVerificationModal } = useMemberVerificationModal();
        const { updateVerificationTags } = useVerificationTags(purchase, purchaseItem);

        const [selectedGuestsForCheckIn, setSelectedGuestsForCheckIn] = useState<Guest[] | null>(null);
        const [isGuestForCheckInModalOpen, { show: showGuestForCheckInModal, hide: hideGuestForCheckInModal }] =
            useVisibility(false);

        const [isGuestNoShowModalOpen, { show: showGuestNoShowModal, hide: hideGuestNoShowModal }] =
            useVisibility(false);

        const totalGuestsCount = purchaseItem.guests.length;
        const {
            areAllGuestsNoShow,
            areAllGuestsCheckedIn,
            isCheckedIn,
            isReturned,
            isNoShow,
            areGuestsCheckedInOrReturnedOnly,
        } = useMemo(() => getCheckInStatusInfo(purchaseItem, guest), [purchaseItem, guest]);

        const isEarlyReturnEnabled = experience?.earlyReturn ?? false;
        // For equipment purchase items, guests are not returned — return applies to equipment units only.
        // Guest rows show no-show instead.
        const showEquipmentReturn =
            !isEquipmentItem(purchaseItem) &&
            isEarlyReturnEnabled &&
            (isReturned || isCheckedIn || areGuestsCheckedInOrReturnedOnly);

        const { mutateAsync: saveGuestStatus, isLoading: isSaving } = useMutation(saveGuestChange);

        const { mutateAsync: updateEvent, isLoading: isUpdating } = useMutation(async (payload: Event) => {
            return api.put(`/events/${payload.id}`, payload);
        });

        const handleGuestStatusChange = useCallback(
            async (
                guestStatus: GuestStatus,
                guests: Guest[],
                doNotIncreaseAvailability?: boolean,
                source?: ButtonSource,
                verificationStatus?: CheckInVerificationStatus,
            ) => {
                setSource(source ?? ButtonSource.NONE);

                const updateTagAction = getVerificationTagUpdateAction(guestStatus, guests, verificationStatus);
                if (updateTagAction) {
                    updateVerificationTags(updateTagAction, verificationStatus);
                }

                await saveGuestStatus({
                    guestStatus,
                    guests,
                    purchaseId: purchase.id,
                    purchaseItemID: purchaseItem.id,
                    verificationStatus,
                    isItemLevel: isItemLevelCheckIn(purchaseItem),
                });

                if (doNotIncreaseAvailability && event) {
                    const capacity = event.max ?? event.quantity.reserved + event.open;
                    const updatedCapacity = guests.length > 1 ? capacity - totalGuestsCount : capacity - 1;
                    if (updatedCapacity >= 0) {
                        await updateEvent({ ...event, max: updatedCapacity });
                    }
                }

                invalidate("purchases", purchase.id);
                invalidate("purchaseItems", purchaseItem.id);
                if (event) {
                    setTimeout(() => {
                        const timeslotParams = {
                            timeslot: null,
                            event: null,
                            productId: purchaseItem.product.id,
                            startDate: purchaseItem.arrivalDate,
                        };

                        const updateParams = {
                            isGranted: isGranted,
                        };

                        updateTimeslotCache(timeslotParams, updateParams);
                    }, 2000); // give some time delay for elastic search to get updated and invalidate to get new data
                }

                invalidate("events", purchaseItem.event.id);

                useDashboardTableStore.setState({ rowToRefresh: `${purchaseItem.id}-${purchase.organizer?.id}` });
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [event, purchaseItem],
        );

        const handleCheckInClick = useCallback(
            async (guestId?: ID) => {
                const guest = purchaseItem.guests.find((guest) => guest.id === guestId);
                const { guests } = purchaseItem;
                const showTicketPrintModal =
                    (guest && guest.guestStatus !== GuestStatus.ARRIVED) || !areAllGuestsCheckedIn;

                // Item-level check-in (no guests) cannot use the per-guest ticket-print modal; apply status
                // directly and let the post-payment flow handle ticket printing. XS-1821.
                if (isTicketPrintingEnabled && showTicketPrintModal && !isItemLevelCheckIn(purchaseItem)) {
                    setSelectedGuestsForCheckIn(guest ? [guest] : guests);
                    showGuestForCheckInModal();
                    return;
                }

                if (guest) {
                    const guestStatus =
                        guest.guestStatus === GuestStatus.ARRIVED ? GuestStatus.PENDING : GuestStatus.ARRIVED;

                    let verificationStatus;
                    if (guestStatus === GuestStatus.ARRIVED && isMemberVerificationRequired()) {
                        verificationStatus = await verifyMember();
                        if (!verificationStatus) return;
                    }

                    handleGuestStatusChange(
                        guestStatus,
                        [guest],
                        false,
                        ButtonSource.GUEST_CHECK_IN,
                        verificationStatus,
                    );
                    return;
                }

                const guestStatus = isCheckedIn ? GuestStatus.PENDING : GuestStatus.ARRIVED;
                let verificationStatus;
                if (
                    guestStatus === GuestStatus.ARRIVED &&
                    getGuestsToBeCheckedIn(guests).length > 0 &&
                    isMemberVerificationRequired()
                ) {
                    verificationStatus = await verifyMember();
                    if (!verificationStatus) return;
                }

                handleGuestStatusChange(guestStatus, guests, false, ButtonSource.GROUP_CHECK_IN, verificationStatus);
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [isCheckedIn, purchaseItem, areAllGuestsCheckedIn, handleGuestStatusChange],
        );

        const handleReturnClick = useCallback(
            async (guestId?: ID) => {
                if (guestId) {
                    const guest = purchaseItem.guests.find((guest) => {
                        return guest.id === guestId;
                    });
                    if (guest) {
                        const guestStatus = isReturned ? GuestStatus.ARRIVED : GuestStatus.RETURNED;
                        handleGuestStatusChange(guestStatus, [guest], false, ButtonSource.GUEST_RETURN_EQUIPMENT);
                    }
                } else {
                    const guestStatus = isReturned ? GuestStatus.ARRIVED : GuestStatus.RETURNED;
                    const { guests } = purchaseItem;
                    handleGuestStatusChange(guestStatus, guests, false, ButtonSource.GROUP_RETURN_EQUIPMENT);
                }
            },
            [isReturned, purchaseItem, handleGuestStatusChange],
        );

        const handleNoShowChange = useCallback(
            async (doNotIncreaseAvailability: boolean, guestId?: ID) => {
                const guestStatus = areAllGuestsNoShow ? GuestStatus.PENDING : GuestStatus.NO_SHOW;

                if (guestId) {
                    const guest = purchaseItem.guests.find((guest) => guest.id === guestId);
                    if (guest) {
                        const updatedStatus =
                            guest.guestStatus === GuestStatus.NO_SHOW ? GuestStatus.PENDING : GuestStatus.NO_SHOW;
                        handleGuestStatusChange(
                            updatedStatus,
                            [guest],
                            doNotIncreaseAvailability,
                            ButtonSource.GUEST_NO_SHOW,
                        );
                    }
                } else {
                    handleGuestStatusChange(
                        guestStatus,
                        purchaseItem.guests,
                        doNotIncreaseAvailability,
                        ButtonSource.GROUP_NO_SHOW,
                    );
                }
            },
            [areAllGuestsNoShow, purchaseItem, handleGuestStatusChange],
        );

        const handleNoShowModal = useCallback(
            async (guestId?: ID) => {
                const guest = purchaseItem.guests.find((guest) => guest.id === guestId);

                // Equipment items reserve resources, not event seats, so the "increase availability" prompt does
                // not apply. Skip the modal and mark no-show directly. XS-1821.
                if (guest && guest.guestStatus === GuestStatus.NO_SHOW) {
                    handleNoShowChange(false, guest.id);
                } else if (areAllGuestsNoShow || isEquipmentItem(purchaseItem)) {
                    handleNoShowChange(false, guest?.id);
                } else {
                    showGuestNoShowModal();
                    setGuestNoShowDetails({
                        count: guest ? 1 : purchaseItem.guests.length,
                        guestId: guest && guestId ? guestId : "",
                    });
                }
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [areAllGuestsNoShow, purchaseItem],
        );

        const shouldShowSpinnerForCheckIn =
            isSaving && [ButtonSource.GUEST_CHECK_IN, ButtonSource.GROUP_CHECK_IN].includes(source);
        const shouldShowSpinnerForNoShow =
            isSaving && [ButtonSource.GUEST_NO_SHOW, ButtonSource.GROUP_NO_SHOW].includes(source);
        const shouldShowSpinnerForReturn =
            isSaving && [ButtonSource.GROUP_RETURN_EQUIPMENT, ButtonSource.GUEST_RETURN_EQUIPMENT].includes(source);

        return (
            <>
                <span className="flex">
                    {guest ? (
                        <CheckInButton
                            className="!mr-2"
                            isDisabled={isSaving || isReturned}
                            active={isCheckedIn}
                            isLoading={shouldShowSpinnerForCheckIn}
                            isGuestLevelButton={!!guest}
                            onClick={async () => handleCheckInClick(guest.id)}
                        />
                    ) : (
                        <Tooltip disabled={!isOrganizer || isReturned} content="Check-in all guests">
                            <CheckInButton
                                className="!mr-2"
                                isDisabled={isSaving || isReturned}
                                active={isCheckedIn}
                                isLoading={shouldShowSpinnerForCheckIn}
                                isGuestLevelButton={!!guest}
                                onClick={async () => handleCheckInClick()}
                            />
                        </Tooltip>
                    )}

                    {showEquipmentReturn ? (
                        <Tooltip content="Return Equipment Early">
                            <CheckInButton
                                isDisabled={isSaving}
                                isLoading={shouldShowSpinnerForReturn}
                                active={isReturned}
                                action="return"
                                onClick={async () => handleReturnClick(guest?.id)}
                            />
                        </Tooltip>
                    ) : guest ? (
                        <CheckInButton
                            action="noShow"
                            isDisabled={isSaving || isReturned}
                            active={isNoShow}
                            isLoading={shouldShowSpinnerForNoShow}
                            isGuestLevelButton={!!guest}
                            onClick={async () => handleNoShowModal(guest?.id)}
                        />
                    ) : (
                        <Tooltip disabled={!isOrganizer} content="No-show all guests">
                            <CheckInButton
                                action="noShow"
                                isDisabled={isSaving || isReturned}
                                active={isNoShow}
                                isLoading={shouldShowSpinnerForNoShow}
                                isGuestLevelButton={!!guest}
                                onClick={async () => handleNoShowModal()}
                            />
                        </Tooltip>
                    )}
                </span>
                {isGuestForCheckInModalOpen && (
                    <CheckInAndPrintTicket
                        isOpen={isGuestForCheckInModalOpen}
                        purchase={purchase}
                        purchaseItem={purchaseItem}
                        guests={selectedGuestsForCheckIn ?? []}
                        onComplete={() => {
                            setSelectedGuestsForCheckIn(null);
                            hideGuestForCheckInModal();
                        }}
                    />
                )}
                {isGuestNoShowModalOpen && (
                    <GuestNoShowModal
                        guestId={noShowGuestDetails?.guestId}
                        count={noShowGuestDetails.count}
                        purchaseItem={purchaseItem}
                        isOpen={isGuestNoShowModalOpen}
                        isLoading={isUpdating}
                        onConfirm={handleNoShowChange}
                        onClose={() => hideGuestNoShowModal()}
                    />
                )}
                <MemberVerificationModal purchaseItem={purchaseItem} />
            </>
        );
    },
);

const saveGuestChange = async ({
    guestStatus,
    guests,
    purchaseId,
    purchaseItemID,
    verificationStatus,
    isItemLevel,
}: SaveGuestStatusPayload) => {
    // Item-level check-in (no guests): the backend applies the status on the item directly. XS-1821.
    if (isItemLevel) {
        await api.patch(`/purchases/${purchaseId}`, {
            items: [{ id: purchaseItemID, guestStatus }],
        });
        return;
    }

    const isPrimaryGuestUpdated = guests.some((guest) => guest.primary);

    await api.patch(`/purchases/${purchaseId}`, {
        items: [
            {
                id: purchaseItemID,
                guests: guests.map((guest, index) => {
                    const guestPayload: Partial<FormGuest> = { id: guest.id, guestStatus };

                    const alreadyCheckedInOnce = [GuestStatus.ARRIVED, GuestStatus.RETURNED].includes(
                        guest.guestStatus,
                    );
                    if (guestStatus === GuestStatus.ARRIVED && !alreadyCheckedInOnce) {
                        /** As we only allow single check-in for scheduled experiences (which are displayed in the roster); `incrementCheckInCount` should be only added when guest status is changed from "noshow" or "pending". No need to check in already checked-in guests  */
                        const shouldSetVerificationState =
                            verificationStatus && (isPrimaryGuestUpdated ? guest.primary : index === 0);
                        guestPayload.incrementCheckInCount = GuestCheckInAction.CHECK_IN;
                        guestPayload.checkIns = [
                            {
                                createdAt: now(),
                                verificationStatus: shouldSetVerificationState ? verificationStatus : undefined,
                            },
                        ];
                    }

                    if (
                        guest.guestStatus === GuestStatus.ARRIVED &&
                        (guestStatus === GuestStatus.PENDING || guestStatus === GuestStatus.NO_SHOW)
                    ) {
                        guestPayload.incrementCheckInCount = GuestCheckInAction.UNDO_CHECK_IN;
                        if (guest.checkIns && guest.checkIns.length > 0) {
                            const lastCheckIn = guest.checkIns.pop()!;
                            guestPayload.checkIns = [{ ...lastCheckIn, deletedAt: now() }];
                        }
                    }

                    return guestPayload;
                }),
            },
        ],
    });
};
